import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { CustomerService, getCRMStats } from '../services/CustomerService';
import { useDatabase } from '../contexts/DatabaseContext';
import SlideOverDrawer from '../components/common/SlideOverDrawer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { CUSTOMER_TYPES, CUSTOMER_TYPE_LABELS } from '../utils/constants';
import { formatDate } from '../utils/formatters';

const {
  FiUsers,
  FiMapPin,
  FiFileText,
  FiPlus,
  FiSearch,
  FiMail,
  FiPhone,
  FiEdit2,
  FiTrash2,
  FiHome,
  FiBriefcase,
  FiUser,
  FiKey,
  FiBuilding,
  FiAlertCircle,
  FiChevronRight
} = FiIcons;

const TYPE_ICONS = {
  [CUSTOMER_TYPES.HOMEOWNER]: FiHome,
  [CUSTOMER_TYPES.LANDLORD]: FiKey,
  [CUSTOMER_TYPES.ESTATE_AGENT]: FiBriefcase,
  [CUSTOMER_TYPES.COMMERCIAL]: FiBuilding
};

const TYPE_COLORS = {
  [CUSTOMER_TYPES.HOMEOWNER]: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300',
  [CUSTOMER_TYPES.LANDLORD]: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
  [CUSTOMER_TYPES.ESTATE_AGENT]: 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300',
  [CUSTOMER_TYPES.COMMERCIAL]: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
};

const EMPTY_FORM = {
  name: '',
  type: CUSTOMER_TYPES.HOMEOWNER,
  email: '',
  phone: '',
  emails: [],
  billingAddress: '',
  notes: ''
};

const Customers = () => {
  const { activeTenant } = useTenant();
  const { isReady } = useDatabase();

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({ customerCount: 0, propertyCount: 0, certificateCount: 0, appliances: 0 });
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null); // null = create
  const [formDraft, setFormDraft] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Detail drawer state
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [customerProperties, setCustomerProperties] = useState([]);
  const [customerCertificates, setCustomerCertificates] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [custs, props, crmStats] = await Promise.all([
        CustomerService.getCustomers(),
        CustomerService.getProperties(),
        getCRMStats()
      ]);
      setCustomers(custs || []);
      setProperties(props || []);
      setStats(crmStats || { customerCount: 0, propertyCount: 0, certificateCount: 0, appliances: 0 });
    } catch (e) {
      console.error('Failed to load CRM data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isReady) loadData();
  }, [isReady, loadData]);

  // Search + filter
  const visibleCustomers = customers.filter((c) => {
    const matchesSearch =
      !searchTerm ||
      [c.name, c.email, c.phone, ...(c.emails || []), c.billingAddress]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || c.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Property lookup map for counts
  const propertyCountByCustomer = properties.reduce((map, p) => {
    map[p.customerId] = (map[p.customerId] || 0) + 1;
    return map;
  }, {});

  const openCreate = () => {
    setEditingCustomer(null);
    setFormDraft(EMPTY_FORM);
    setError(null);
    setIsEditorOpen(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setFormDraft({
      name: customer.name || '',
      type: customer.type || CUSTOMER_TYPES.HOMEOWNER,
      email: customer.email || '',
      phone: customer.phone || '',
      emails: customer.emails || [],
      billingAddress: customer.billingAddress || '',
      notes: customer.notes || ''
    });
    setError(null);
    setIsEditorOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setFormDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddEmail = () => {
    setFormDraft((prev) => ({ ...prev, emails: [...(prev.emails || []), ''] }));
  };

  const handleEmailChange = (idx, value) => {
    setFormDraft((prev) => {
      const emails = [...(prev.emails || [])];
      emails[idx] = value;
      return { ...prev, emails };
    });
  };

  const handleRemoveEmail = (idx) => {
    setFormDraft((prev) => ({
      ...prev,
      emails: (prev.emails || []).filter((_, i) => i !== idx)
    }));
  };

  const handleSave = async () => {
    if (!formDraft.name.trim()) {
      setError('Customer name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formDraft,
        // Promote first non-empty additional email into primary if primary empty
        email: formDraft.email.trim() || (formDraft.emails || []).find((e) => e.trim()) || '',
        emails: (formDraft.emails || []).filter((e) => e.trim())
      };
      if (editingCustomer) {
        await CustomerService.updateCustomer(editingCustomer.id, payload);
      } else {
        await CustomerService.createCustomer(payload);
      }
      setIsEditorOpen(false);
      await loadData();
    } catch (e) {
      console.error('Save customer failed:', e);
      setError(e.message || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete customer "${customer.name}"? Their properties and certificate links remain intact.`)) return;
    try {
      await CustomerService.deleteCustomer(customer.id);
      if (detailCustomer?.id === customer.id) {
        setDetailCustomer(null);
        setCustomerProperties([]);
        setCustomerCertificates([]);
      }
      await loadData();
    } catch (e) {
      console.error('Delete customer failed:', e);
      window.alert(`Failed to delete customer: ${e.message}`);
    }
  };

  const openDetail = async (customer) => {
    setDetailCustomer(customer);
    try {
      const [props, certs] = await Promise.all([
        CustomerService.getPropertiesByCustomer(customer.id),
        CustomerService.getCustomerCertificates(customer.id)
      ]);
      setCustomerProperties(props || []);
      setCustomerCertificates(certs || []);
    } catch (e) {
      console.error('Failed to load customer detail:', e);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
              Customer Directory
            </h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {customers.length} Customers
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Organization: <strong className="text-slate-700 dark:text-slate-300">{activeTenant.name}</strong> • Homeowners, landlords, agents & contractors
          </p>
        </div>

        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-md shadow-blue-900/20 transition-all active:scale-95"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          Add Customer
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Customers', value: stats.customerCount, icon: FiUsers, color: 'bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400' },
          { label: 'Properties', value: stats.propertyCount, icon: FiMapPin, color: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400' },
          { label: 'Certificates', value: stats.certificateCount, icon: FiFileText, color: 'bg-purple-100 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400' },
          { label: 'Registered Appliances', value: stats.appliances, icon: FiKey, color: 'bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400' }
        ].map((card) => (
          <div key={card.label} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{card.label}</p>
                <p className="text-2xl font-black text-slate-900 dark:text-slate-100 mt-1">{card.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.color}`}>
                <SafeIcon icon={card.icon} className="w-5 h-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search customers by name, email, phone..."
            className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="sm:w-56">
          <select
            className="w-full py-2 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Customer Types</option>
            {Object.values(CUSTOMER_TYPES).map((type) => (
              <option key={type} value={type}>{CUSTOMER_TYPE_LABELS[type]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Customer Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : visibleCustomers.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-12 text-center">
          <SafeIcon icon={FiUsers} className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">No customers found</p>
          <p className="text-xs text-slate-400 mb-4">
            {searchTerm || typeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Add your first customer to start building your property portfolio'}
          </p>
          {!searchTerm && typeFilter === 'all' && (
            <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold">
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Add Customer
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCustomers.map((customer) => {
            const TypeIcon = TYPE_ICONS[customer.type] || FiUser;
            const typeColor = TYPE_COLORS[customer.type] || TYPE_COLORS[CUSTOMER_TYPES.HOMEOWNER];
            return (
              <div
                key={customer.id}
                onClick={() => openDetail(customer)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-300 flex-shrink-0">
                      <TypeIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {customer.name}
                      </h3>
                      <span className={`inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${typeColor}`}>
                        {CUSTOMER_TYPE_LABELS[customer.type] || customer.type}
                      </span>
                    </div>
                  </div>
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>

                <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400 mb-4">
                  {(customer.email || customer.emails?.length) && (
                    <p className="flex items-center gap-2 truncate">
                      <SafeIcon icon={FiMail} className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{customer.email || customer.emails[0]}</span>
                    </p>
                  )}
                  {customer.phone && (
                    <p className="flex items-center gap-2">
                      <SafeIcon icon={FiPhone} className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span>{customer.phone}</span>
                    </p>
                  )}
                  {customer.billingAddress && (
                    <p className="flex items-center gap-2 truncate">
                      <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{customer.billingAddress}</span>
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                    {propertyCountByCustomer[customer.id] || 0} Properties • Added {formatDate(customer.createdAt)}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openEdit(customer)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                      title="Edit Customer"
                    >
                      <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(customer)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                      title="Delete Customer"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Customer Drawer */}
      <SlideOverDrawer
        isOpen={isEditorOpen}
        onClose={() => !saving && setIsEditorOpen(false)}
        title={editingCustomer ? 'Edit Customer' : 'Add Customer'}
        subtitle={editingCustomer ? `Updating ${editingCustomer.name}` : 'Create a new contact in your directory'}
        width="max-w-xl"
        footer={
          <div className="w-full flex justify-end gap-2">
            <button
              onClick={() => !saving && setIsEditorOpen(false)}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : editingCustomer ? 'Save Changes' : 'Add Customer'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs border border-red-200 dark:border-red-900">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Full Name *</label>
            <input
              type="text"
              value={formDraft.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="e.g. Sarah Thompson"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Customer Type</label>
              <select
                value={formDraft.type}
                onChange={(e) => handleFieldChange('type', e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-700 dark:text-slate-200 focus:outline-none"
              >
                {Object.values(CUSTOMER_TYPES).map((type) => (
                  <option key={type} value={type}>{CUSTOMER_TYPE_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Phone</label>
              <input
                type="tel"
                value={formDraft.phone}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                placeholder="020 7946 0000"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Primary Email</label>
            <input
              type="email"
              value={formDraft.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="sarah@example.com"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Additional Emails */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">Additional Emails</label>
              <button
                onClick={handleAddEmail}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700"
              >
                <SafeIcon icon={FiPlus} className="w-3 h-3" />
                Add Email
              </button>
            </div>
            <div className="space-y-2">
              {(formDraft.emails || []).map((email, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(idx, e.target.value)}
                    placeholder={`email${idx + 1}@example.com`}
                    className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => handleRemoveEmail(idx)}
                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                    title="Remove email"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Billing Address</label>
            <textarea
              value={formDraft.billingAddress}
              onChange={(e) => handleFieldChange('billingAddress', e.target.value)}
              rows={2}
              placeholder="Billing address for invoices (landlords / agents)"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Notes</label>
            <textarea
              value={formDraft.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              rows={2}
              placeholder="Internal notes about this customer"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>
      </SlideOverDrawer>

      {/* Customer Detail Drawer */}
      <SlideOverDrawer
        isOpen={!!detailCustomer}
        onClose={() => setDetailCustomer(null)}
        title={detailCustomer?.name}
        subtitle={detailCustomer ? `${CUSTOMER_TYPE_LABELS[detailCustomer.type] || detailCustomer.type} • Added ${formatDate(detailCustomer.createdAt)}` : ''}
        width="max-w-2xl"
        footer={
          detailCustomer && (
            <div className="w-full flex items-center justify-between gap-2">
              <Link
                to={`/properties?customerId=${detailCustomer.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5" />
                View Properties ({customerProperties.length})
              </Link>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(detailCustomer)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(detailCustomer)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/80 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          )
        }
      >
        {detailCustomer && (
          <div className="space-y-6">
            {/* Contact Details */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">Contact Details</h4>
              <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3 space-y-2 text-xs">
                <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <SafeIcon icon={FiMail} className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold">Primary:</span>
                  <span className="truncate">{detailCustomer.email || '—'}</span>
                </p>
                {(detailCustomer.emails || []).filter(Boolean).map((email, idx) => (
                  <p key={idx} className="flex items-center gap-2 text-slate-700 dark:text-slate-200 pl-5">
                    <SafeIcon icon={FiMail} className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-semibold">Email {idx + 2}:</span>
                    <span className="truncate">{email}</span>
                  </p>
                ))}
                <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <SafeIcon icon={FiPhone} className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold">Phone:</span>
                  <span>{detailCustomer.phone || '—'}</span>
                </p>
                <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold">Billing:</span>
                  <span>{detailCustomer.billingAddress || '—'}</span>
                </p>
                {detailCustomer.notes && (
                  <p className="text-slate-600 dark:text-slate-300 pt-1 border-t border-slate-200 dark:border-slate-700">
                    {detailCustomer.notes}
                  </p>
                )}
              </div>
            </div>

            {/* Properties */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Properties ({customerProperties.length})
                </h4>
                <Link
                  to={`/properties?customerId=${detailCustomer.id}&new=1`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700"
                >
                  <SafeIcon icon={FiPlus} className="w-3 h-3" />
                  Add Property
                </Link>
              </div>
              {customerProperties.length === 0 ? (
                <p className="text-xs text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
                  No properties registered for this customer yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {customerProperties.map((prop) => (
                    <Link
                      key={prop.id}
                      to={`/properties?focus=${prop.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition-all group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                            {prop.addressLine1}
                            {prop.city ? `, ${prop.city}` : ''}
                          </p>
                          <p className="text-[10px] text-slate-400">{prop.postcode}</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-semibold text-slate-400 flex-shrink-0">
                        {prop.appliances?.length || 0} appliances
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Certificate History */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                Certificate History ({customerCertificates.length})
              </h4>
              {customerCertificates.length === 0 ? (
                <p className="text-xs text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
                  No certificates linked to this customer yet. Create one from a property on the Properties page.
                </p>
              ) : (
                <div className="space-y-2">
                  {customerCertificates.map((cert) => (
                    <Link
                      key={cert.id}
                      to={`/forms/${cert.formId}/${cert.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition-all group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <SafeIcon icon={FiFileText} className="w-4 h-4 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                            {cert.instanceId || `${cert.formId} #${cert.id}`}
                          </p>
                          <p className="text-[10px] text-slate-400">{formatDate(cert.updatedAt)}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                        cert.status === 'completed' || cert.status === 'issued'
                          ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300'
                          : 'bg-yellow-100 dark:bg-yellow-950/40 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {cert.status}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SlideOverDrawer>
    </div>
  );
};

export default Customers;